import React, { useEffect, useState, useRef } from 'react';
import { FileText, Upload, Trash2, X, AlertCircle, Clock, CheckCircle, XCircle } from 'lucide-react';
import { fetchDocuments, uploadDocument, deleteDocument } from '../../api/supplierApi';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { toast } from 'sonner';

const API_BASE = process.env.REACT_APP_BACKEND_URL;

const DOC_TYPES = [
  { value: 'trade_license', label: { tr: 'Ticaret Lisansı', en: 'Trade License' } },
  { value: 'insurance', label: { tr: 'Sigorta', en: 'Insurance' } },
  { value: 'certificate', label: { tr: 'Sertifika', en: 'Certificate' } },
  { value: 'company_profile', label: { tr: 'Firma Profili', en: 'Company Profile' } },
  { value: 'catalog', label: { tr: 'Katalog', en: 'Catalog' } },
  // Turkish company documents
  { value: 'turkish_registry_gazette', label: { tr: 'Türkiye Ticaret Sicili Gazetesi', en: 'Turkish Registry Gazette' } },
  { value: 'tax_registration_certificate', label: { tr: 'Vergi Levhası', en: 'Tax Registration Certificate' } },
  { value: 'certificate_of_activity', label: { tr: 'Faaliyet Belgesi', en: 'Certificate of Activity' } },
  { value: 'signature_circular', label: { tr: 'İmza Sirküleri', en: 'Signature Circular' } },
  { value: 'other', label: { tr: 'Diğer', en: 'Other' } },
];

const DOC_STATUS = {
  pending: { icon: Clock, color: 'text-orange-500', bg: 'bg-orange-50', label: { tr: 'İncelemede', en: 'Pending' } },
  approved: { icon: CheckCircle, color: 'text-emerald-500', bg: 'bg-emerald-50', label: { tr: 'Onaylandı', en: 'Approved' } },
  rejected: { icon: XCircle, color: 'text-red-500', bg: 'bg-red-50', label: { tr: 'Reddedildi', en: 'Rejected' } },
};

const EMPTY = { document_type: '', title: '', expiry_date: '' };

function UploadModal({ tr, onClose, onUploaded }) {
  const [form, setForm] = useState({ ...EMPTY });
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const handleUpload = async () => {
    if (!form.document_type) { toast.error(tr ? 'Belge türü seçin.' : 'Select document type.'); return; }
    if (!form.title.trim()) { toast.error(tr ? 'Belge başlığı girin.' : 'Enter a title.'); return; }
    if (!file) { toast.error(tr ? 'Dosya seçin.' : 'Select a file.'); return; }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('document_type', form.document_type);
      fd.append('title', form.title);
      if (form.expiry_date) fd.append('expiry_date', form.expiry_date);
      fd.append('file', file);
      const doc = await uploadDocument(fd);
      toast.success(tr ? 'Belge yüklendi.' : 'Document uploaded.');
      onUploaded(doc);
      onClose();
    } catch (e) {
      toast.error(e.response?.data?.detail || (tr ? 'Yükleme başarısız.' : 'Upload failed.'));
    } finally { setUploading(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm" onClick={onClose}>
      <div className="bg-white rounded-3xl shadow-2xl w-full max-w-md" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between px-6 pt-6 pb-4 border-b">
          <h3 className="font-bold text-gray-900">{tr ? 'Belge Yükle' : 'Upload Document'}</h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600"><X className="h-5 w-5" /></button>
        </div>
        <div className="px-6 py-5 space-y-4">
          <div className="space-y-1.5">
            <Label className="text-xs font-semibold text-gray-500 uppercase">{tr ? 'Belge Türü' : 'Document Type'} *</Label>
            <select value={form.document_type} onChange={e => set('document_type', e.target.value)} className="w-full rounded-xl border border-gray-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-amber-300">
              <option value="">{tr ? '-- Seçin --' : '-- Select --'}</option>
              {DOC_TYPES.map(d => <option key={d.value} value={d.value}>{tr ? d.label.tr : d.label.en}</option>)}
            </select>
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs font-semibold text-gray-500 uppercase">{tr ? 'Başlık' : 'Title'} *</Label>
            <Input value={form.title} onChange={e => set('title', e.target.value)} className="rounded-xl" placeholder={tr ? 'Örn: 2024 Ticaret Sicil Belgesi' : 'e.g. 2024 Trade License'} />
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs font-semibold text-gray-500 uppercase">{tr ? 'Son Geçerlilik Tarihi (opsiyonel)' : 'Expiry Date (optional)'}</Label>
            <Input type="date" value={form.expiry_date} onChange={e => set('expiry_date', e.target.value)} className="rounded-xl" />
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs font-semibold text-gray-500 uppercase">{tr ? 'Dosya' : 'File'} *</Label>
            <input ref={fileRef} type="file" accept=".pdf,.doc,.docx,.jpg,.jpeg,.png,.ppt,.pptx" className="hidden" onChange={e => setFile(e.target.files?.[0] || null)} />
            {file ? (
              <div className="flex items-center gap-2 p-3 bg-amber-50 border border-amber-200 rounded-xl">
                <FileText className="h-4 w-4 text-amber-600 shrink-0" />
                <span className="text-sm text-gray-700 flex-1 truncate">{file.name}</span>
                <span className="text-xs text-amber-600 font-medium">{(file.size / 1024).toFixed(0)} KB</span>
                <button onClick={() => setFile(null)} className="text-gray-400 hover:text-red-500"><X className="h-4 w-4" /></button>
              </div>
            ) : (
              <button onClick={() => fileRef.current?.click()} className="w-full flex items-center justify-center gap-2 p-3 border-2 border-dashed border-amber-200 rounded-xl text-amber-600 hover:bg-amber-50 text-sm font-medium">
                <Upload className="h-4 w-4" />{tr ? 'Dosya Seç (PDF, DOC, PPT, görsel)' : 'Browse File (PDF, DOC, PPT, image)'}
              </button>
            )}
            <p className="text-xs text-gray-400">{tr ? 'Maks. 20 MB' : 'Max 20 MB'}</p>
          </div>
        </div>
        <div className="px-6 pb-6 flex gap-3">
          <Button onClick={handleUpload} disabled={uploading} className="flex-1 bg-amber-500 hover:bg-amber-600 text-white rounded-xl">
            {uploading ? (tr ? 'Yükleniyor...' : 'Uploading...') : (tr ? 'Yükle' : 'Upload')}
          </Button>
          <Button variant="outline" onClick={onClose} className="rounded-xl">{tr ? 'İptal' : 'Cancel'}</Button>
        </div>
      </div>
    </div>
  );
}

export default function DocumentsSection({ tr }) {
  const [docs, setDocs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [deleting, setDeleting] = useState(null);

  const load = () => { setLoading(true); fetchDocuments().then(setDocs).catch(() => toast.error(tr ? 'Belgeler yüklenemedi.' : 'Failed to load documents.')).finally(() => setLoading(false)); };
  useEffect(load, []);

  const handleDelete = async (doc) => {
    if (!window.confirm(tr ? `"${doc.title}" belgesini silmek istiyor musunuz?` : `Delete "${doc.title}"?`)) return;
    setDeleting(doc.id);
    try { await deleteDocument(doc.id); setDocs(prev => prev.filter(d => d.id !== doc.id)); toast.success(tr ? 'Belge silindi.' : 'Document deleted.'); }
    catch (e) { toast.error(e.response?.data?.detail || (tr ? 'Silinemedi.' : 'Delete failed.')); }
    finally { setDeleting(null); }
  };

  const getDocTypeLabel = (val) => { const dt = DOC_TYPES.find(d => d.value === val); return dt ? (tr ? dt.label.tr : dt.label.en) : val; };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">{tr ? 'Belgelerim' : 'My Documents'}</h2>
          <p className="text-sm text-gray-500 mt-0.5">{docs.length} {tr ? 'belge' : 'documents'}</p>
        </div>
        <Button onClick={() => setShowModal(true)} className="bg-amber-500 hover:bg-amber-600 text-white rounded-xl">
          <Upload className="h-4 w-4 mr-2" />{tr ? 'Belge Yükle' : 'Upload Document'}
        </Button>
      </div>

      <div className="bg-amber-50 border border-amber-200 rounded-2xl p-4 flex items-start gap-3">
        <AlertCircle className="h-4 w-4 text-amber-600 mt-0.5 shrink-0" />
        <p className="text-sm text-amber-700">{tr ? 'Yüklenen belgeler Ultra Admin tarafından incelenir ve onaylanır.' : 'Uploaded documents are reviewed and approved by Ultra Admin.'}</p>
      </div>

      {loading ? (
        <div className="flex justify-center h-48 items-center"><div className="animate-spin h-10 w-10 border-4 border-amber-500 border-t-transparent rounded-full" /></div>
      ) : docs.length === 0 ? (
        <div className="flex flex-col items-center h-48 justify-center text-center bg-white rounded-2xl border border-gray-100">
          <FileText className="h-12 w-12 text-gray-200 mb-3" />
          <p className="text-gray-500 font-medium">{tr ? 'Henüz belge yüklenmedi.' : 'No documents uploaded yet.'}</p>
          <Button size="sm" onClick={() => setShowModal(true)} className="mt-4 bg-amber-500 text-white rounded-xl">{tr ? 'İlk belgeyi yükle' : 'Upload your first document'}</Button>
        </div>
      ) : (
        <div className="grid md:grid-cols-2 gap-4">
          {docs.map(doc => {
            const st = DOC_STATUS[doc.status] || DOC_STATUS.pending;
            const StatusIcon = st.icon;
            return (
              <div key={doc.id} className="bg-white rounded-2xl shadow-sm border border-gray-100 p-5">
                <div className="flex items-start justify-between mb-3">
                  <div className="flex items-start gap-3">
                    <div className={`h-10 w-10 rounded-xl ${st.bg} flex items-center justify-center shrink-0`}>
                      <FileText className={`h-5 w-5 ${st.color}`} />
                    </div>
                    <div>
                      <p className="font-semibold text-gray-900 text-sm">{doc.title}</p>
                      <p className="text-xs text-gray-400 mt-0.5">{getDocTypeLabel(doc.document_type)}</p>
                    </div>
                  </div>
                  <Button size="sm" variant="ghost" onClick={() => handleDelete(doc)} disabled={deleting === doc.id} className="h-8 w-8 p-0 text-red-400 hover:bg-red-50 rounded-lg shrink-0">
                    {deleting === doc.id ? <div className="animate-spin h-3.5 w-3.5 border-2 border-red-400 border-t-transparent rounded-full" /> : <Trash2 className="h-3.5 w-3.5" />}
                  </Button>
                </div>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-1.5">
                    <StatusIcon className={`h-3.5 w-3.5 ${st.color}`} />
                    <span className={`text-xs font-medium ${st.color}`}>{tr ? st.label.tr : st.label.en}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    {doc.expiry_date && <span className="text-xs text-gray-400">{tr ? 'Son: ' : 'Exp: '}{new Date(doc.expiry_date).toLocaleDateString()}</span>}
                    <a href={`${API_BASE}${doc.file_url}`} target="_blank" rel="noopener noreferrer" className="text-xs text-amber-600 hover:text-amber-800 font-medium">
                      {tr ? 'Görüntüle' : 'View'}
                    </a>
                  </div>
                </div>
                <p className="text-xs text-gray-300 mt-2">{new Date(doc.uploaded_at).toLocaleString()}</p>
              </div>
            );
          })}
        </div>
      )}

      {showModal && <UploadModal tr={tr} onClose={() => setShowModal(false)} onUploaded={doc => setDocs(prev => [doc, ...prev])} />}
    </div>
  );
}
